import type { ChatToolEvent, ContextSource, CopilotRunMeta } from '@fos/shared/contracts/copilot';
import { COPILOT_MESSAGES } from '@fos/shared/contracts/copilot';
import { aiProviderLabel } from '@fos/shared/labels';
import { Dec, NBSP } from '@fos/shared/money';
import { appErrorCode, errorMessage } from '../../lib/api';
import { formatMoney, formatRelativePast } from '../../lib/format';

/**
 * Wspólne teksty i odnośniki Copilota (MVP §11, §12; DESIGN §2.5 „AI zawsze widoczne jako AI”): stan błędu AI,
 * podpis wyniku („Podsumowanie AI, dziś 7:30 · Symulacja”), koszt w ułamkach grosza, adresy źródeł i rekordów.
 */

export type CopilotErrorState = 'disabled' | 'budget_exceeded' | 'unavailable' | 'forbidden' | 'not_found' | 'failed';

export interface CopilotErrorInfo {
  state: CopilotErrorState;
  message: string;
  /** funkcje ręczne działają (AI wyłączone, niedostępne, budżet) */
  manualWorks: boolean;
}

function detailsState(err: unknown): string | null {
  const details = (err as { data?: { details?: { state?: unknown } } } | null)?.data?.details;
  return typeof details?.state === 'string' ? details.state : null;
}

/** Błąd akcji Copilota → stan i komunikat dla UI (zawsze z informacją, że ręczna ścieżka działa). */
export function copilotErrorInfo(err: unknown): CopilotErrorInfo {
  const code = appErrorCode(err);
  if (code === 'RATE_LIMITED') {
    return { state: 'budget_exceeded', message: errorMessage(err, COPILOT_MESSAGES.budgetExceeded), manualWorks: true };
  }
  if (code === 'INTEGRATION_UNAVAILABLE') {
    const disabled = detailsState(err) === 'disabled';
    return {
      state: disabled ? 'disabled' : 'unavailable',
      message: errorMessage(err, disabled ? COPILOT_MESSAGES.disabled : COPILOT_MESSAGES.unavailable),
      manualWorks: true,
    };
  }
  if (code === 'FORBIDDEN')
    return { state: 'forbidden', message: errorMessage(err, 'Brak uprawnień.'), manualWorks: false };
  if (code === 'NOT_FOUND') {
    return { state: 'not_found', message: 'Nie znaleziono danych albo nie masz do nich dostępu.', manualWorks: false };
  }
  return { state: 'failed', message: errorMessage(err, COPILOT_MESSAGES.unavailable), manualWorks: false };
}

/** Koszt AI (4 miejsca w bazie): ułamek grosza jako „< 0,01 zł”, inaczej z groszami. */
export function formatAiCost(amount: string | null | undefined): string {
  if (amount === null || amount === undefined) return '—';
  const d = new Dec(amount);
  if (d.isZero()) return formatMoney('0');
  if (d.lt('0.01')) return `<${NBSP}0,01${NBSP}zł`;
  return formatMoney(amount);
}

/** Dostawca w podpisie: symulacja nie udaje realnego modelu. */
export function providerText(meta: Pick<CopilotRunMeta, 'provider' | 'model' | 'simulated' | 'source'>): string {
  if (meta.source === 'rules') return 'bez AI (reguły)';
  if (meta.simulated) return 'Symulacja';
  return meta.model ? `${aiProviderLabel(meta.provider)} · ${meta.model}` : aiProviderLabel(meta.provider);
}

/** Podpis wyniku AI: „Podsumowanie AI, dziś, 7:30 · Symulacja”. */
export function metaLine(kind: string, meta: CopilotRunMeta, now: Date = new Date()): string {
  const when = formatRelativePast(meta.generatedAt, now);
  const parts = [`${kind}, ${when}`];
  if (meta.source === 'rules') parts.push('bez AI');
  else if (meta.simulated) parts.push('Symulacja');
  else parts.push(providerText(meta));
  if (meta.costPln !== undefined && !meta.simulated && meta.source === 'ai') parts.push(formatAiCost(meta.costPln));
  return parts.join(' · ');
}

/** Adres rekordu źródła (link „Na podstawie: …”); null = bez strony (np. tekst wklejony). */
export function sourceHref(source: Pick<ContextSource, 'type' | 'id'>): string | null {
  if (!source.id) return null;
  switch (source.type) {
    case 'client':
      return `/klienci/${source.id}`;
    case 'project':
      return `/projekty/${source.id}`;
    case 'task':
      return `/zadania/${source.id}`;
    case 'knowledge_item':
      return `/wiedza/${source.id}`;
    case 'meeting':
      return `/spotkania/${source.id}`;
    case 'offer':
      return `/oferty/${source.id}`;
    case 'hosted_asset':
      return `/utrzymanie/${source.id}`;
    case 'approval':
      return `/akceptacje/${source.id}`;
    case 'message_thread':
      return `/poczta/${source.id}`;
    case 'context_profile':
      return '/ustawienia/ai';
    default:
      return null;
  }
}

/** Adres rekordu z wyniku narzędzia „Zapytaj AI” (zadanie, akceptacja, klient, propozycja pamięci). */
export function toolEventHref(event: Pick<ChatToolEvent, 'link' | 'approvalId'>): string | null {
  if (event.approvalId) return `/akceptacje/${event.approvalId}`;
  const link = event.link;
  if (!link) return null;
  switch (link.type) {
    case 'task':
      return `/zadania/${link.id}`;
    case 'client':
      return `/klienci/${link.id}`;
    case 'approval':
      return `/akceptacje/${link.id}`;
    case 'memory_proposal':
      return '/ustawienia/ai';
    // Fala 10: działania Copilota (spotkanie, notatka w Wiedzy, strona w Utrzymaniu, awaria)
    case 'meeting':
      return `/spotkania/${link.id}`;
    case 'knowledge_item':
      return `/wiedza/${link.id}`;
    case 'hosted_asset':
      return `/utrzymanie/${link.id}`;
    case 'incident':
      return `/incydenty/${link.id}`;
    // Fala 10 (notatki klienta): notatka nie ma własnej strony — zakładka „Historia” klienta
    case 'client_note':
      return link.clientId ? `/klienci/${link.clientId}?zakladka=historia` : null;
    // 17.10.2026: szybka notatka (karteczka) — sekcja „Szybkie notatki” na Dziś
    case 'quick_note':
      return '/dzis';
    default:
      return null;
  }
}
